import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  AlertCircle,
  CheckCircle,
  TrendingDown,
  Activity,
  Heart,
  CalendarIcon,
} from "lucide-react";
import {
  DosageCalculatorProps,
  CalculatorResult,
} from "../dosageCalculators/types";
import { ethnicityOptions, getBMICategoryByEthnicity } from "@/lib/vitalsData";
import { usePrescriptionsState } from "@/lib/dashboard/prescriptionsData";

interface WeightLossMedication {
  prescribeItemId: string;
  name: string;
  activeIngredient: string;
  dosageSchedule: {
    week: number;
    dose: string;
    instruction: string;
  }[];
  initialSupply: string;
  notes: string;
  contraindications: string[];
  suitableFor: string[];
  medicationType: string;
}

const weightLossMedications: WeightLossMedication[] = [
  {
    prescribeItemId: "f6f0430d-cea8-442f-bde1-446f94ca96ce",
    name: "Semaglutide Injection",
    activeIngredient: "Semaglutide",
    dosageSchedule: [
      { week: 1, dose: "0.25mg", instruction: "Inject 0.25mg once weekly" },
      { week: 5, dose: "0.5mg", instruction: "Inject 0.5mg once weekly" },
      { week: 9, dose: "1mg", instruction: "Inject 1mg once weekly" },
      { week: 13, dose: "1.7mg", instruction: "Inject 1.7mg once weekly" },
      {
        week: 17,
        dose: "2.4mg",
        instruction: "Inject 2.4mg once weekly (maintenance)",
      },
    ],
    initialSupply: "4 x 0.25mg pre-filled pens",
    notes:
      "Titration schedule over 16-20 weeks. Inject once weekly on the same day each week. Can be taken with or without food. Inject subcutaneously in abdomen, thigh, or upper arm. Rotate injection sites.",
    contraindications: [
      "Personal or family history of medullary thyroid carcinoma",
      "Multiple Endocrine Neoplasia syndrome type 2",
      "Pregnancy or breastfeeding",
      "Type 1 diabetes",
      "Diabetic ketoacidosis",
    ],
    suitableFor: [
      "Adults with BMI ≥30 kg/m²",
      "Adults with BMI ≥27 kg/m² with weight-related comorbidities",
    ],
    medicationType: "Injection",
  },
  {
    prescribeItemId: "5b999332-388f-4530-a0c1-2906b8e33fe8",
    name: "Semaglutide Tablets",
    activeIngredient: "Semaglutide",
    dosageSchedule: [
      { week: 1, dose: "1.5mg", instruction: "Take ONE 1.5mg tablet once a day" },
      { week: 2, dose: "4mg", instruction: "Take ONE 4mg tablet once a day" },
      { week: 3, dose: "9mg", instruction: "Take ONE 9mg tablet once a day" },
      { week: 4, dose: "25mg", instruction: "Take ONE 25mg tablet once a day" },
    ],
    initialSupply: "Semaglutide tablets (oral)",
    notes:
      "Take one tablet once daily on an empty stomach after a recommended fasting period of at least 8 hours. Swallow the tablet whole with a sip of water (up to 120 mL). Do not split, crush or chew the tablet, as it is not known if it affects absorption of semaglutide. After taking the tablet, wait at least 30 minutes before eating or drinking or taking other oral medicines.",
    contraindications: [
      "Personal or family history of medullary thyroid carcinoma",
      "Multiple Endocrine Neoplasia syndrome type 2",
      "Pregnancy or breastfeeding",
      "Type 1 diabetes",
      "Diabetic ketoacidosis",
    ],
    suitableFor: [
      "Adults with BMI ≥30 kg/m²",
      "Adults with BMI ≥27 kg/m² with weight-related comorbidities",
    ],
    medicationType: "Tablet",
  },
  {
    prescribeItemId: "k9x_00000000-0000-0000-0000-abc000000009",
    name: "Liraglutide Injection",
    activeIngredient: "Liraglutide",
    dosageSchedule: [
      { week: 1, dose: "0.6mg", instruction: "Inject 0.6mg once daily" },
      { week: 2, dose: "1.2mg", instruction: "Inject 1.2mg once daily" },
      { week: 3, dose: "1.8mg", instruction: "Inject 1.8mg once daily" },
      { week: 4, dose: "2.4mg", instruction: "Inject 2.4mg once daily" },
      {
        week: 5,
        dose: "3mg",
        instruction: "Inject 3mg once daily (maintenance)",
      },
    ],
    initialSupply: "1 x 6mg/ml pre-filled pen (contains 18mg total)",
    notes:
      "Titration schedule over 5 weeks. Inject once daily at any time, with or without food. Inject subcutaneously in abdomen, thigh, or upper arm. Rotate injection sites. Each pen lasts approximately 6 days at starting dose.",
    contraindications: [
      "Personal or family history of medullary thyroid carcinoma",
      "Multiple Endocrine Neoplasia syndrome type 2",
      "Pregnancy or breastfeeding",
      "Inflammatory bowel disease",
      "Diabetic gastroparesis",
    ],
    suitableFor: [
      "Adults with BMI ≥30 kg/m²",
      "Adults with BMI ≥27 kg/m² with weight-related comorbidities",
    ],
    medicationType: "Injection",
  },
  {
    prescribeItemId: "5024151e-328e-4e9a-8315-eb0686ba9164",
    name: "Tirzepatide Injection",
    activeIngredient: "Tirzepatide",
    dosageSchedule: [
      { week: 1, dose: "2.5mg", instruction: "Inject 2.5mg once weekly" },
      { week: 5, dose: "5mg", instruction: "Inject 5mg once weekly" },
      { week: 9, dose: "7.5mg", instruction: "Inject 7.5mg once weekly" },
      { week: 13, dose: "10mg", instruction: "Inject 10mg once weekly" },
      { week: 17, dose: "12.5mg", instruction: "Inject 12.5mg once weekly" },
      {
        week: 21,
        dose: "15mg",
        instruction: "Inject 15mg once weekly (maximum)",
      },
    ],
    initialSupply: "4 x 2.5mg pre-filled pens",
    notes:
      "Titration schedule with dose increases every 4 weeks. Inject once weekly on the same day. Can be taken with or without food. Inject subcutaneously in abdomen, thigh, or upper arm. Rotate injection sites.",
    contraindications: [
      "Personal or family history of medullary thyroid carcinoma",
      "Multiple Endocrine Neoplasia syndrome type 2",
      "Pregnancy or breastfeeding",
      "Type 1 diabetes",
      "History of pancreatitis",
    ],
    suitableFor: [
      "Adults with BMI ≥30 kg/m²",
      "Adults with BMI ≥27 kg/m² with weight-related comorbidities",
    ],
    medicationType: "Injection",
  },
];

const SEMAGLUTIDE_TABLETS_ITEM_ID = "5b999332-388f-4530-a0c1-2906b8e33fe8";

const tirzepatideStrengthToPrescribeItemId: Record<string, string> = {
  "5mg": "b5ba485e-89bb-46ae-8b5c-0f65589ad627",
  "2.5mg": "5024151e-328e-4e9a-8315-eb0686ba9164",
  "7.5mg": "e7e17093-37ef-4a90-90cf-3c12dc25742c",
  "10mg": "f0e40f69-1afa-4d20-8cd9-aa3cf27a72f0",
  "12.5mg": "6d654f9d-b1a8-4a0e-a10c-454b23f02083",
  "15mg": "c6048448-9341-463b-860e-c005e0807053",
};

const semaglutideStrengthToPrescribeItemId: Record<string, string> = {
  "0.25mg": "c57a2c8c-d54d-4ec5-82be-54ad622813c4",
  "0.5mg": "fd0fb041-2efe-46cd-82cd-e76c56fc9cc3",
  "1mg": "b6a66277-1749-4573-a842-dcbcf27da7be",
  "1.7mg": "51e28b46-2e08-4366-ba46-6bd428f7e2d0",
  "2.4mg": "f6f0430d-cea8-442f-bde1-446f94ca96ce",
};

const semaglutideTabletsStrengthToPrescribeItemId: Record<string, string> = {
  "1.5mg": "5b999332-388f-4530-a0c1-2906b8e33fe8",
  "4mg": "cb009197-4d09-4a22-af88-055887e47e89",
  "9mg": "234ad62d-6205-4e5e-ae22-48095e5827e1",
  "25mg": "a9319208-934a-4a67-a2ed-375dc60b59b1",
};

export const WeightLossCalculator: React.FC<DosageCalculatorProps> = ({
  onComplete,
  onCancel,
  patientInfo,
  isDarkMode = false,
  patientVitals,
}) => {
  const mapEthnicity = (ethnicityValue?: number | string | null) => {
    if (typeof ethnicityValue !== "number") return ethnicityValue || "";
    const ethnicityMap: Record<number, string> = {
      0: "Not Specified",
      1: "White - British",
      2: "White - Irish",
      3: "White - Any other White background",
      4: "Mixed - White and Black Caribbean",
      5: "Mixed - White and Black African",
      6: "Mixed - White and Asian",
      7: "Mixed - Any other mixed background",
      8: "Asian or Asian British - Indian",
      9: "Asian or Asian British - Pakistani",
      10: "Asian or Asian British - Bangladeshi",
      11: "Asian or Asian British - Any other Asian background",
      12: "Black or Black British - Caribbean",
      13: "Black or Black British - African",
      14: "Black or Black British - Any other Black background",
      15: "Other Ethnic Groups - Chinese",
      16: "Other Ethnic Groups - Any other ethnic group",
    };
    return ethnicityMap[ethnicityValue] || "";
  };

  const convertHeightToCm = (value: string, unit: string): string => {
    if (unit === "ft/in") {
      // Handles formats like "5 ft 5 in", "5ft 5in", "5 ft", etc.
      const match = value?.match(
        /(\d+(?:\.\d+)?)\s*ft\s*(?:(\d+(?:\.\d+)?)\s*in)?/i,
      );
      if (match) {
        const feet = parseFloat(match[1]) || 0;
        const inches = parseFloat(match[2]) || 0;
        const cm = feet * 30.48 + inches * 2.54;
        return Math.round(cm).toString();
      }
    }
    return value;
  };
  const getWaistInCm = (circumference: any): string => {
    if (!circumference?.value) return "";

    const rawValue = circumference.value;
    const unit = circumference.unit?.toLowerCase();

    // Extract Waist number using regex
    const match = rawValue.match(/Waist:\s*(\d+(\.\d+)?)/i);
    if (!match) return "";

    let waist = parseFloat(match[1]);

    // Convert inch → cm
    if (unit === "in" || unit === "inch" || unit === "inches") {
      waist = waist * 2.54;
    }

    // Return rounded value (optional)
    return waist.toFixed(1); // e.g. "215.9"
  };

  const getInitialVital = (vitalName: string) => {
    if (!patientVitals) return "";
    
    const key = vitalName.toLowerCase();
    // Special mappings for nested vitals
    if (key === "blood pressure systolic") {
      return patientVitals.bloodPressure?.systolic?.value || "";
    }
    if (key === "blood pressure diastolic") {
      return patientVitals.bloodPressure?.diastolic?.value || "";
    }
    if (key === "heartrate") {
      return patientVitals.heartRate?.value || "";
    }

    if (key === "circumference") {
      return getWaistInCm(patientVitals.circumference);
    }

    // Default for flat vitals like height, weight
    let vital: any = patientVitals[key];
    if (!vital) return "";

    // Height conversion
    if (key === "height") {
      return convertHeightToCm(vital.value, vital.unit);
    }

    // Generic "Waist: 80" handling (though not needed here)
    if (typeof vital?.value === "string" && vital.value.includes(":")) {
      const parts = vital.value.split(":");
      if (parts.length > 1) return parts[1].trim();
    }

    return vital?.value || "";
  };

  const [step, setStep] = useState<"patient-info" | "medication">(
    "patient-info",
  );
  const [selectedMedication, setSelectedMedication] = useState<string>("");
  const [selectedStrength, setSelectedStrength] = useState<string>("");
  const [saxendaPhase, setSaxendaPhase] = useState<
    "titration" | "maintenance" | ""
  >("");
  const [saxendaTitrationStrengths, setSaxendaTitrationStrengths] = useState<
    string[]
  >(["", "", "", ""]);
  const [startDate, setStartDate] = useState<string>("");
  const [currentWeight, setCurrentWeight] = useState<string>(() =>
    getInitialVital("Weight"),
  );
  const [height, setHeight] = useState<string>(() => getInitialVital("Height"));
  const [targetWeightLoss, setTargetWeightLoss] = useState<string>("");
  const [hasContraindications, setHasContraindications] =
    useState<string>("no");
  const [bmiResult, setBmiResult] = useState<number | null>(null);
  const [ethnicity, setEthnicity] = useState<string>(() =>
    mapEthnicity(patientInfo?.ethnicity),
  );

  // Optional vitals
  const [systolicBP, setSystolicBP] = useState<string>(() =>
    getInitialVital("Blood Pressure Systolic"),
  );
  const [diastolicBP, setDiastolicBP] = useState<string>(() =>
    getInitialVital("Blood Pressure Diastolic"),
  );
  const [pulse, setPulse] = useState<string>(() =>
    getInitialVital("HeartRate"),
  );
  const [waistCircumference, setWaistCircumference] = useState<string>(() =>
    getInitialVital("Circumference"),
  );

  const { medicationHistory } = usePrescriptionsState();

  // Get previous weight loss prescriptions from medication history
  const previousSupplies = React.useMemo(() => {
    if (!Array.isArray(medicationHistory)) return [];

    const matching = medicationHistory.filter((entry: any) => {
      const name = entry.medicationName?.toLowerCase() || "";
      return (
        name.includes("wegovy") ||
        name.includes("semaglutide") ||
        name.includes("saxenda") ||
        name.includes("liraglutide") ||
        name.includes("mounjaro") ||
        name.includes("tirzepatide")
      );
    });

    const latestByMedication = new Map<string, any>();

    matching.forEach((entry: any) => {
      const key =
        entry.id ||
        entry.medicationName?.toLowerCase().trim() ||
        entry.medication?.toLowerCase().trim() ||
        "";
      const existing = latestByMedication.get(key);
      if (!existing || new Date(entry.date) > new Date(existing.date)) {
        latestByMedication.set(key, entry);
      }
    });

    return Array.from(latestByMedication.values()).sort(
      (a: any, b: any) =>
        new Date(b.date).getTime() - new Date(a.date).getTime(),
    );
  }, [medicationHistory]);

  const extractStrengthFromSupply = (supply: any): string => {
    const strengthFromNotes = supply.notes?.match(
      /Selected Strength:\s*([\d.]+mg)/i,
    )?.[1];
    if (strengthFromNotes) return strengthFromNotes;

    const strengthFromDose = supply.dose?.match(/([\d.]+mg)/i)?.[1];
    if (strengthFromDose) return strengthFromDose;

    const strengthFromName = supply.medicationName?.match(/([\d.]+mg)/i)?.[1];
    if (strengthFromName) return strengthFromName;

    return "";
  };

  const handlePreviousSupplyClick = (supply: any) => {
    const medicationText = supply.medicationName || supply.medication || "";
    const medicationLower = medicationText.toLowerCase();

    const matchedMedication =
      weightLossMedications.find((m) => m.prescribeItemId === supply.id) ||
      weightLossMedications.find(
        (m) =>
          medicationLower.includes(m.activeIngredient.toLowerCase()) ||
          medicationLower.includes(m.name.toLowerCase()),
      );

    if (!matchedMedication) {
      setSelectedMedication("");
      setSelectedStrength("");
      return;
    }

    setSelectedMedication(matchedMedication.prescribeItemId);

    const strength = extractStrengthFromSupply(supply);
    if (strength) {
      setSelectedStrength(strength);
    } else {
      setSelectedStrength("");
    }

    setStep("medication");
  };

  const effectivePrescribeItemId = React.useMemo(() => {
    if (!selectedMedication) return "";

    const medicationDefinition = weightLossMedications.find(
      (m) => m.prescribeItemId === selectedMedication,
    );

    if (!medicationDefinition) return selectedMedication;

    const ingredient = medicationDefinition.activeIngredient.toLowerCase();
    if (ingredient === "tirzepatide") {
      return (
        tirzepatideStrengthToPrescribeItemId[selectedStrength] ||
        medicationDefinition.prescribeItemId
      );
    }

    if (ingredient === "semaglutide" && medicationDefinition.medicationType === "Injection") {
      return (
        semaglutideStrengthToPrescribeItemId[selectedStrength] ||
        medicationDefinition.prescribeItemId
      );
    }

    if (ingredient === "semaglutide" && medicationDefinition.medicationType === "Tablet"){
      return(
        semaglutideTabletsStrengthToPrescribeItemId[selectedStrength] ||
        medicationDefinition.prescribeItemId
      );
    }

    return medicationDefinition.prescribeItemId;
  }, [selectedMedication, selectedStrength]);

  // No automatic default selection from previous supplies or patient medication history.
  // The previous medications are displayed in the Previous Supplies section,
  // and the user must explicitly choose medication and strength from the selects.

  // Calculate BMI when weight and height are provided
  React.useEffect(() => {
    if (currentWeight && height) {
      const weightKg = parseFloat(currentWeight);
      const heightM = parseFloat(height) / 100; // Convert cm to m
      if (weightKg > 0 && heightM > 0) {
        const bmi = weightKg / (heightM * heightM);
        setBmiResult(parseFloat(bmi.toFixed(1)));
      }
    } else {
      setBmiResult(null);
    }
  }, [currentWeight, height]);

  const medication = weightLossMedications.find(
    (m) =>
      m.prescribeItemId === selectedMedication || m.name === selectedMedication,
  );

  const isSaxendaTitration =
    medication?.prescribeItemId ===
      "k9x_00000000-0000-0000-0000-abc000000009" &&
    saxendaPhase === "titration";

  // Helper function to format date as "24 Jan 2026"
  const formatDate = (date: Date): string => {
    const day = date.getDate();
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day.toString().padStart(2, "0")} ${month} ${year}`;
  };

  // Helper function to calculate weekly injection dates
  const calculateWeeklyDates = (
    startDate: Date,
    numberOfWeeks: number,
  ): string[] => {
    const dates: string[] = [];
    for (let i = 0; i < numberOfWeeks; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i * 7);
      dates.push(formatDate(date));
    }
    return dates;
  };

  const handleCalculate = () => {
    if (!medication || !currentWeight || !ethnicity) {
      return;
    }

    if (
      medication.prescribeItemId === "k9x_00000000-0000-0000-0000-abc000000009"
    ) {
      if (!saxendaPhase) return;
      if (
        saxendaPhase === "titration" &&
        saxendaTitrationStrengths.some((s) => !s)
      ) {
        return;
      }
      if (saxendaPhase === "maintenance" && !selectedStrength) {
        return;
      }
    } else if (!selectedStrength) {
      return;
    }

    // For non-titration flows we use a single dose schedule entry.
    const primaryStrength = isSaxendaTitration
      ? saxendaTitrationStrengths[0]
      : selectedStrength;

    const selectedDoseSchedule = medication.dosageSchedule.find(
      (schedule) => schedule.dose === primaryStrength,
    );

    if (!selectedDoseSchedule) return;

    const maintenanceDose =
      medication.dosageSchedule[medication.dosageSchedule.length - 1];

    // Calculate supply quantity (4 weeks worth)
    const supplyQuantity =
      medication.prescribeItemId === "k9x_00000000-0000-0000-0000-abc000000009"
        ? "1 x 6mg/ml pre-filled pens (4 weeks supply)"
        : `1 x ${selectedStrength} ${medication.medicationType === "Tablet" ? "tablets" : "pre-filled pens"}`;

    const hasStartDate = !!startDate;
    const programmeStartDate = hasStartDate ? new Date(startDate) : null;
    const weeklyDates =
      programmeStartDate !== null
        ? calculateWeeklyDates(programmeStartDate, 4)
        : [];

    // Review date = 7 days after the last supply date
    let reviewDateStr = "";
    if (programmeStartDate !== null) {
      const lastSupply = new Date(programmeStartDate);
      lastSupply.setDate(lastSupply.getDate() + 3 * 7);
      const reviewDate = new Date(lastSupply);
      reviewDate.setDate(reviewDate.getDate() + 7);
      reviewDateStr = formatDate(reviewDate);
    }

    // Build the dosage instruction based on selected strength(s)
    const isSemaglutideTablets =
      medication.prescribeItemId === SEMAGLUTIDE_TABLETS_ITEM_ID;
    let dosageInstruction: string;
    if (isSaxendaTitration) {
      const lines = saxendaTitrationStrengths.map((dose, i) => {
        const sched = medication.dosageSchedule.find((s) => s.dose === dose);
        const dateLabel = weeklyDates[i] ? ` on ${weeklyDates[i]}` : "";
        return `Week ${i + 1}: ${sched ? sched.instruction : `Inject ${dose} once daily`}${dateLabel}.`;
      });
      dosageInstruction = `Saxenda titration schedule:\n${lines.join("\n")}`;
      if (hasStartDate && reviewDateStr) {
        dosageInstruction += `\n\nPlease book a review on or before ${reviewDateStr}.`;
      }
    } else {
      if(medication?.medicationType === "Tablet"){
        dosageInstruction = `${selectedDoseSchedule.instruction}`;
      }
      else{
        dosageInstruction = `${selectedDoseSchedule.instruction}. Inject once weekly on the same day each week.`;
      }
      if (hasStartDate && programmeStartDate !== null) {
        dosageInstruction = `${selectedDoseSchedule.instruction} on:\n${weeklyDates.join(", ")}.\n\nPlease book a review on or before ${reviewDateStr}.`;
      }
      // Semaglutide tablets use the administration guidance as the dosage text.
      if (isSemaglutideTablets) {
        dosageInstruction = medication.notes;
      }
    }

    const strengthLabel = isSaxendaTitration
      ? saxendaTitrationStrengths.join(" → ")
      : selectedStrength;

    const duration = programmeStartDate
      ? `4 weeks supply at ${strengthLabel} (starting ${formatDate(programmeStartDate)})`
      : `4 weeks supply at ${strengthLabel}`;

    // Get BMI category based on ethnicity
    const bmiCategory = bmiResult
      ? getBMICategoryByEthnicity(bmiResult, ethnicity)
      : "";

    // Collect vitals that were entered
    const vitals: Array<{ type: string; value: string; unit?: string }> = [];

    // Add weight and height (always required)
    vitals.push({ type: "weight", value: currentWeight, unit: "kg" });
    vitals.push({ type: "height", value: height, unit: "cm" });

    // Add BMI
    if (bmiResult) {
      vitals.push({
        type: "bmi",
        value: bmiResult.toString(),
        unit: `kg/m² (${bmiCategory})`,
      });
    }

    // Add optional vitals if entered
    if (systolicBP && diastolicBP) {
      vitals.push({
        type: "bloodPressure",
        value: `${systolicBP}/${diastolicBP}`,
        unit: "mmHg",
      });
    }

    if (pulse) {
      vitals.push({ type: "pulse", value: pulse, unit: "bpm" });
    }

    if (waistCircumference) {
      vitals.push({
        type: "Circumference",
        value: waistCircumference,
        unit: "cm",
      });
    }

    // Resolve per-strength prescribe item id for Mounjaro
    const prescribedItemId =
      effectivePrescribeItemId || medication.prescribeItemId;

    const results: CalculatorResult[] = [
      {
        medication: `${medication.name} - ${strengthLabel}${medication.prescribeItemId === "k9x_00000000-0000-0000-0000-abc000000009" && saxendaPhase ? ` (${saxendaPhase === "titration" ? "Titration" : "Maintenance"})` : ""}`,
        dosage: dosageInstruction,
        quantity: supplyQuantity,
        duration: duration,
        notes: isSemaglutideTablets
          ? ""
          : `${medication.notes}${programmeStartDate ? `\n\nStart Date: ${formatDate(programmeStartDate)}` : ""}\nSelected Strength: ${strengthLabel}\n${selectedDoseSchedule.instruction}${weeklyDates.length > 0 ? `\n\nWeekly Injection Dates:\n${weeklyDates.map((d, i) => `  Week ${i + 1}: ${d}`).join("\n")}` : ""}${reviewDateStr ? `\n\nReview on or before: ${reviewDateStr}` : ""}${bmiResult ? `\n\nCurrent BMI: ${bmiResult} kg/m² (${bmiCategory})` : ""}${ethnicity ? `\nEthnicity: ${ethnicity}` : ""}${targetWeightLoss ? `\nTarget Weight Loss: ${targetWeightLoss}kg` : ""}`,
        vitals: vitals,
        prescribedItemId: prescribedItemId,
      },
    ];

    onComplete(results);
  };

  const saxendaSelectionsValid =
    medication?.prescribeItemId !==
      "k9x_00000000-0000-0000-0000-abc000000009" ||
    (saxendaPhase === "maintenance" && !!selectedStrength) ||
    (saxendaPhase === "titration" &&
      saxendaTitrationStrengths.every((s) => !!s));

  const canCalculate =
    selectedMedication &&
    (medication?.prescribeItemId === "k9x_00000000-0000-0000-0000-abc000000009"
      ? saxendaSelectionsValid
      : !!selectedStrength) &&
    currentWeight &&
    height &&
    ethnicity &&
    hasContraindications === "no" &&
    bmiResult !== null;

  const isBmiSuitable = bmiResult !== null && bmiResult >= 27;

  // Step validation
  const canProceedFromPatientInfo =
    currentWeight && height && ethnicity && bmiResult !== null;

  // Navigation functions
  const handleNext = () => {
    if (step === "patient-info" && canProceedFromPatientInfo) {
      setStep("medication");
    }
  };

  const handleBack = () => {
    if (step === "medication") {
      setStep("patient-info");
    }
  };

  return (
    <div
      className={`flex flex-col h-full ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
    >
      {/* Scrollable content - flex-1 to push footer down when overflowing */}
      <div className="flex-1 min-h-0 overflow-y-auto space-y-6 p-4 pr-1">
        {/* Step 1: Patient Information */}
        {step === "patient-info" && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label
                htmlFor="startDate"
                className={isDarkMode ? "text-gray-200" : "text-gray-900"}
              >
                <span className="flex items-center gap-1.5">
                  <CalendarIcon className="w-4 h-4" />
                  Start Date
                </span>
              </Label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <p
                className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Date the weight management programme begins
              </p>
            </div>
            {/* Weight and Height */}
            <div className="space-y-2">
              <Label htmlFor="weight">Current Weight (kg) *</Label>
              <Input
                id="weight"
                type="number"
                placeholder="e.g., 85"
                value={currentWeight}
                onChange={(e) => setCurrentWeight(e.target.value)}
                min="30"
                max="300"
                step="0.1"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="height">Height (cm) *</Label>
              <Input
                id="height"
                type="number"
                placeholder="e.g., 170"
                value={height}
                onChange={(e) => setHeight(e.target.value)}
                min="100"
                max="250"
              />
            </div>

            {/* Ethnicity */}
            <div className="space-y-2">
              <Label
                htmlFor="ethnicity"
                className={isDarkMode ? "text-gray-200" : "text-gray-900"}
              >
                Ethnicity *
              </Label>
              <Select value={ethnicity} onValueChange={setEthnicity}>
                <SelectTrigger id="ethnicity">
                  <SelectValue placeholder="Select ethnicity" />
                </SelectTrigger>
                <SelectContent>
                  {ethnicityOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p
                className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                BMI thresholds are adjusted based on ethnicity (NICE guidelines)
              </p>
            </div>

            {/* BMI Display */}
            {bmiResult !== null && ethnicity && (
              <Card
                className={
                  isBmiSuitable
                    ? isDarkMode
                      ? "bg-green-900/20 border-green-700"
                      : "bg-green-50 border-green-200"
                    : isDarkMode
                      ? "bg-amber-900/20 border-amber-700"
                      : "bg-amber-50 border-amber-200"
                }
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    {isBmiSuitable ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-amber-600" />
                    )}
                    <div className="flex-1">
                      <p
                        className={`text-sm font-medium ${isBmiSuitable ? (isDarkMode ? "text-green-300" : "text-green-900") : isDarkMode ? "text-amber-300" : "text-amber-900"}`}
                      >
                        BMI: {bmiResult} kg/m² (
                        {getBMICategoryByEthnicity(bmiResult, ethnicity)})
                      </p>
                      <p
                        className={`text-xs ${isBmiSuitable ? (isDarkMode ? "text-green-400" : "text-green-700") : isDarkMode ? "text-amber-400" : "text-amber-700"}`}
                      >
                        {isBmiSuitable
                          ? "BMI is within suitable range for weight loss medication"
                          : "BMI below 27 kg/m² - weight loss medication may not be indicated"}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Optional Vitals Section */}
            <Card
              className={
                isDarkMode
                  ? "bg-blue-900/20 border-blue-700"
                  : "bg-blue-50 border-blue-200"
              }
            >
              <CardContent className="p-4 space-y-4">
                <div className="flex items-center gap-2">
                  <Activity className="w-5 h-5 text-blue-600" />
                  <h4
                    className={`text-sm font-semibold ${isDarkMode ? "text-blue-300" : "text-blue-900"}`}
                  >
                    Additional Vitals (Optional)
                  </h4>
                </div>
                <p
                  className={`text-xs ${isDarkMode ? "text-blue-400" : "text-blue-700"}`}
                >
                  Recording these vitals will add them to the patient's records
                </p>

                {/* Blood Pressure */}
                <div className="space-y-2">
                  <Label
                    className={`text-sm ${isDarkMode ? "text-blue-300" : "text-blue-900"}`}
                  >
                    Blood Pressure (mmHg)
                  </Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      type="number"
                      placeholder="Systolic (e.g., 120)"
                      value={systolicBP}
                      onChange={(e) => setSystolicBP(e.target.value)}
                      min="60"
                      max="250"
                      className="bg-white"
                    />
                    <Input
                      type="number"
                      placeholder="Diastolic (e.g., 80)"
                      value={diastolicBP}
                      onChange={(e) => setDiastolicBP(e.target.value)}
                      min="40"
                      max="150"
                      className="bg-white"
                    />
                  </div>
                </div>

                {/* Pulse */}
                <div className="space-y-2">
                  <Label
                    htmlFor="pulse"
                    className={`text-sm ${isDarkMode ? "text-blue-300" : "text-blue-900"}`}
                  >
                    Pulse (bpm)
                  </Label>
                  <Input
                    id="pulse"
                    type="number"
                    placeholder="e.g., 72"
                    value={pulse}
                    onChange={(e) => setPulse(e.target.value)}
                    min="40"
                    max="200"
                    className="bg-white"
                  />
                </div>

                {/* Waist Circumference */}
                <div className="space-y-2">
                  <Label
                    htmlFor="waist"
                    className={`text-sm ${isDarkMode ? "text-blue-300" : "text-blue-900"}`}
                  >
                    Waist Circumference (cm)
                  </Label>
                  <Input
                    id="waist"
                    type="number"
                    placeholder="e.g., 95"
                    value={waistCircumference}
                    onChange={(e) => setWaistCircumference(e.target.value)}
                    min="50"
                    max="200"
                    step="0.1"
                    className="bg-white"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Target Weight Loss */}
            <div className="space-y-2">
              <Label
                htmlFor="target"
                className={isDarkMode ? "text-gray-200" : "text-gray-900"}
              >
                Target Weight Loss (kg)
              </Label>
              <Input
                id="target"
                type="number"
                placeholder="e.g., 10"
                value={targetWeightLoss}
                onChange={(e) => setTargetWeightLoss(e.target.value)}
                min="1"
                max="100"
                step="0.5"
              />
              <p
                className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Optional: Enter your weight loss goal
              </p>
            </div>
          </div>
        )}

        {/* Step 2: Medication Selection */}
        {step === "medication" && (
          <div className="space-y-4">
            {/* Previous Supplies - Clickable */}
            <Card
              className={
                isDarkMode
                  ? "bg-blue-900/20 border-blue-700"
                  : "bg-blue-50 border-blue-200"
              }
            >
              <CardContent className="p-4">
                <h4
                  className={`text-sm font-semibold mb-2 ${isDarkMode ? "text-blue-300" : "text-blue-900"}`}
                >
                  Previous Supplies
                </h4>
                {previousSupplies.length > 0 ? (
                  <>
                    <p
                      className={`text-xs mb-3 ${isDarkMode ? "text-blue-400" : "text-blue-700"}`}
                    >
                      Click to continue with the same medication or choose a new
                      one below
                    </p>
                    <div className="space-y-2">
                      {previousSupplies
                        .slice(0, 3)
                        .map((supply: any, idx: number) => {
                          const medicationText =
                            supply.medicationName || supply.medication || "";
                          const medicationLower = medicationText.toLowerCase();

                          const matchedMedication =
                            weightLossMedications.find(
                              (m) => m.prescribeItemId === supply.id,
                            ) ||
                            weightLossMedications.find(
                              (m) =>
                                medicationLower.includes(
                                  m.activeIngredient.toLowerCase(),
                                ) ||
                                medicationLower.includes(m.name.toLowerCase()),
                            );

                          const medicationId =
                            matchedMedication?.prescribeItemId || "";
                          const isSelected =
                            selectedMedication === medicationId;

                          return (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => handlePreviousSupplyClick(supply)}
                              className={`w-full text-left text-xs p-3 rounded border-2 transition-all ${
                                isSelected
                                  ? isDarkMode
                                    ? "bg-blue-900/40 border-blue-500 shadow-sm"
                                    : "bg-blue-100 border-blue-500 shadow-sm"
                                  : isDarkMode
                                    ? "bg-gray-800 border-blue-700 hover:border-blue-500 hover:bg-blue-900/20"
                                    : "bg-white border-blue-200 hover:border-blue-400 hover:bg-blue-50"
                              }`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1">
                                  <div
                                    className={`font-medium ${isDarkMode ? "text-blue-300" : "text-blue-900"}`}
                                  >
                                    {medicationText}
                                  </div>
                                  <div
                                    className={`mt-1 space-y-0.5 ${isDarkMode ? "text-blue-400" : "text-blue-700"}`}
                                  >
                                    {(supply.dose || supply.dosage) && (
                                      <div>
                                        Dose: {supply.dose || supply.dosage}
                                      </div>
                                    )}
                                    {supply.quantity && (
                                      <div>Quantity: {supply.quantity}</div>
                                    )}
                                    {supply.date && (
                                      <div>
                                        Date:{" "}
                                        {new Date(
                                          supply.date,
                                        ).toLocaleDateString()}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                {isSelected && (
                                  <div className="flex-shrink-0">
                                    <CheckCircle className="w-5 h-5 text-blue-600" />
                                  </div>
                                )}
                              </div>
                            </button>
                          );
                        })}
                    </div>
                  </>
                ) : (
                  <div
                    className={`text-xs p-3 rounded border ${isDarkMode ? "text-blue-400 bg-blue-900/30 border-blue-600" : "text-blue-700 bg-blue-100/50 border-blue-300"}`}
                  >
                    <p className="font-medium mb-1">
                      No previous weight loss medication found
                    </p>
                    <p>
                      This patient has not been prescribed weight loss
                      medication before. Please select a medication below to
                      begin treatment.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Medication Selection Dropdown */}
            <div className="space-y-2">
              <Label
                htmlFor="medication"
                className={isDarkMode ? "text-gray-200" : "text-gray-900"}
              >
                {previousSupplies.length > 0
                  ? "Or select a different medication *"
                  : "Select a medication to prescribe *"}
              </Label>
              <Select
                value={selectedMedication}
                onValueChange={setSelectedMedication}
              >
                <SelectTrigger id="medication">
                  <SelectValue placeholder="Choose weight loss medication..." />
                </SelectTrigger>
                <SelectContent>
                  {weightLossMedications?.map((option) => (
                    <SelectItem
                      key={option.prescribeItemId}
                      value={option.prescribeItemId}
                    >
                      {option.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Saxenda Phase Selection */}
            {medication?.prescribeItemId ===
              "k9x_00000000-0000-0000-0000-abc000000009" && (
              <div className="space-y-2">
                <Label
                  className={isDarkMode ? "text-gray-200" : "text-gray-900"}
                >
                  Saxenda Phase *
                </Label>
                <RadioGroup
                  value={saxendaPhase}
                  onValueChange={(v) => {
                    setSaxendaPhase(v as "titration" | "maintenance");
                    setSelectedStrength("");
                    setSaxendaTitrationStrengths(["", "", "", ""]);
                  }}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="titration" id="phase-titration" />
                    <Label
                      htmlFor="phase-titration"
                      className="font-normal cursor-pointer"
                    >
                      Titration
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="maintenance"
                      id="phase-maintenance"
                    />
                    <Label
                      htmlFor="phase-maintenance"
                      className="font-normal cursor-pointer"
                    >
                      Maintenance
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            )}

            {/* Strength Selection */}
            {medication &&
              !(
                medication.prescribeItemId ===
                  "k9x_00000000-0000-0000-0000-abc000000009" &&
                saxendaPhase === "titration"
              ) &&
              (medication.prescribeItemId !==
                "k9x_00000000-0000-0000-0000-abc000000009" ||
                saxendaPhase === "maintenance") && (
                <div className="space-y-2">
                  <Label
                    htmlFor="strength"
                    className={isDarkMode ? "text-gray-200" : "text-gray-900"}
                  >
                    Select Strength *
                  </Label>
                  <Select
                    value={selectedStrength}
                    onValueChange={setSelectedStrength}
                  >
                    <SelectTrigger id="strength">
                      <SelectValue placeholder="Choose strength..." />
                    </SelectTrigger>
                    <SelectContent>
                      {medication.dosageSchedule.map((schedule) => (
                        <SelectItem key={schedule.dose} value={schedule.dose}>
                          {schedule.dose} - {schedule.instruction}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p
                    className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Select the appropriate strength for this supply
                  </p>
                </div>
              )}

            {/* Saxenda Titration: 4 Strength Selectors */}
            {medication?.prescribeItemId ===
              "k9x_00000000-0000-0000-0000-abc000000009" &&
              saxendaPhase === "titration" && (
                <div className="space-y-2">
                  <Label
                    className={isDarkMode ? "text-gray-200" : "text-gray-900"}
                  >
                    Select Strengths (4 instances) *
                  </Label>
                  <div className="space-y-2">
                    {saxendaTitrationStrengths.map((value, idx) => (
                      <div key={idx} className="space-y-1">
                        <Label
                          htmlFor={`titration-strength-${idx}`}
                          className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                        >
                          Week {idx + 1}
                        </Label>
                        <Select
                          value={value || ""}
                          onValueChange={(v) => {
                            const next = [...saxendaTitrationStrengths];
                            next[idx] = v;
                            setSaxendaTitrationStrengths(next);
                          }}
                        >
                          <SelectTrigger id={`titration-strength-${idx}`}>
                            <SelectValue
                              placeholder={`Choose strength for week ${idx + 1}...`}
                            >
                              {value ||
                                `Choose strength for week ${idx + 1}...`}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {medication.dosageSchedule.map((schedule) => (
                              <SelectItem
                                key={schedule.dose}
                                value={schedule.dose}
                              >
                                {schedule.dose} - {schedule.instruction}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                  <p
                    className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Select the strength for each of the 4 titration instances
                  </p>
                </div>
              )}

            {/* Contraindications Check */}
            <div className="space-y-3">
              <Label
                className={`text-base font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
              >
                Contraindications Check *
              </Label>
              {medication && (
                <Card
                  className={
                    isDarkMode
                      ? "bg-red-900/20 border-red-700"
                      : "bg-red-50 border-red-200"
                  }
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p
                          className={`text-sm font-medium mb-2 ${isDarkMode ? "text-red-300" : "text-red-900"}`}
                        >
                          Contraindications for {medication.name}:
                        </p>
                        <ul
                          className={`text-xs space-y-1 list-disc list-inside ${isDarkMode ? "text-red-400" : "text-red-700"}`}
                        >
                          {medication.contraindications.map((contra, idx) => (
                            <li key={idx}>{contra}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              <RadioGroup
                value={hasContraindications}
                onValueChange={setHasContraindications}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="contra-no" />
                  <Label
                    htmlFor="contra-no"
                    className="font-normal cursor-pointer"
                  >
                    Patient has NO contraindications listed above
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="contra-yes" />
                  <Label
                    htmlFor="contra-yes"
                    className="font-normal cursor-pointer"
                  >
                    Patient has one or more contraindications
                  </Label>
                </div>
              </RadioGroup>

              {hasContraindications === "yes" && (
                <Card
                  className={
                    isDarkMode
                      ? "bg-red-900/20 border-red-700"
                      : "bg-red-50 border-red-200"
                  }
                >
                  <CardContent className="p-3">
                    <p
                      className={`text-sm ${isDarkMode ? "text-red-300" : "text-red-800"}`}
                    >
                      ⚠️ This medication is contraindicated for this patient.
                      Please consider alternative options or refer to
                      specialist.
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Suitable For Information */}
            {medication && (
              <Card
                className={
                  isDarkMode
                    ? "bg-gray-800/30 border-gray-700"
                    : "bg-gray-50 border-gray-200"
                }
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-2">
                    <TrendingDown className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p
                        className={`text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-900"}`}
                      >
                        Suitable for:
                      </p>
                      <ul
                        className={`text-xs space-y-1 list-disc list-inside ${isDarkMode ? "text-gray-400" : "text-gray-700"}`}
                      >
                        {medication.suitableFor.map((suitable, idx) => (
                          <li key={idx}>{suitable}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
      {/* end scrollable content */}

      {/* Flexible footer - flows naturally with content, sticks when content overflows */}
      <div
        className={`flex items-center gap-3 pt-4 border-t flex-shrink-0 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
      >
        {step !== "patient-info" ? (
          <Button variant="outline" onClick={handleBack} className="flex-1">
            Back
          </Button>
        ) : (
          <Button variant="outline" onClick={onCancel} className="flex-1">
            Cancel
          </Button>
        )}
        {step === "medication" ? (
          <Button
            onClick={handleCalculate}
            disabled={!canCalculate}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
          >
            Summarise
          </Button>
        ) : (
          <Button
            onClick={handleNext}
            disabled={!canProceedFromPatientInfo}
            className="flex-1 bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50"
          >
            Continue
          </Button>
        )}
      </div>
    </div>
  );
};
